// src/Pages/Dashboard/Organization.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import "./Dashboard.css";

const Organization = () => {
  const profile = JSON.parse(localStorage.getItem("profile") || "{}");
  const userId = profile.id || profile.oid || profile.sub;

  const [form, setForm] = useState({
    logo: null,
    name: "Demo Application",
    industry: "Technology",
    location: "India",
    address: {
      street1: "",
      street2: "",
      city: "",
      pinCode: "",
      state: "Tamil Nadu",
      phone: "",
      fax: "",
    },
    website: "",
    differentPaymentAddress: false,
    paymentStubAddress: "",
    primaryContact: {
      senderName: "Super Admin",
      senderEmail: "mailadmin@zustpe.com",
    },
    emailSender: "mailadmin@zustpe.com",
    baseCurrency: "INR",
    fiscalYear: "April - March",
    reportBasis: "Accrual",
    language: "English",
    communicationLanguage: "English",
    timeZone: "(GMT +5:30) India Standard Time (Asia/Calcutta)",
    dateFormat: "dd/MM/yyyy",
    dateSeparator: "/",
    companyIdPrefix: "ANC",
    companyIdValue: "",
    additionalFields: [{ label: "", value: "" }],
  });

  const [logoPreview, setLogoPreview] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!userId) return;
    axios
      .get(
        `${
          process.env.REACT_APP_BACKEND_URL || window.location.origin
        }/api/organization/get?userId=${userId}`
      )
      .then((res) => {
        if (res.data.success && res.data.data) {
          setForm(res.data.data);
          if (res.data.data.logo) setLogoPreview(res.data.data.logo);
        }
      })
      .catch((err) => console.log("Load error:", err));
  }, [userId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.includes("address.")) {
      const field = name.split(".")[1];
      setForm({ ...form, address: { ...form.address, [field]: value } });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
        setForm({ ...form, logo: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const addField = () => {
    setForm({
      ...form,
      additionalFields: [...form.additionalFields, { label: "", value: "" }],
    });
  };

  const updateField = (i, field, value) => {
    const fields = [...form.additionalFields];
    fields[i][field] = value;
    setForm({ ...form, additionalFields: fields });
  };

  const validate = () => {
    const err = {};
    if (!form.name.trim()) err.name = "Required";
    if (!form.industry) err.industry = "Required";
    if (!form.location.trim()) err.location = "Required";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    if (!userId) return alert("User not logged in!");

    try {
      const response = await axios.post(
        `${
          process.env.REACT_APP_BACKEND_URL || window.location.origin
        }/api/organization/save`,
        { ...form, userId },
        { withCredentials: true }
      );
      if (response.data.success) {
        alert("Organization saved successfully!");
      } else {
        alert("Error: " + response.data.error);
      }
    } catch (err) {
      console.error("Save failed:", err.response?.data || err);
      alert("Error: " + (err.response?.data?.error || err.message));
    }
  };

  return (
    <div className="organization-wrapper">
      <div className="dashboard-main">
        <div className="organization-container">
          {/* Logo Upload */}
          <div className="form-section">
            <label>Organization Logo</label>
            <div className="logo-upload">
              <input
                type="file"
                accept="image/*"
                onChange={handleFile}
                id="logo-input"
              />
              <label htmlFor="logo-input">
                {logoPreview ? (
                  <img src={logoPreview} alt="Logo" />
                ) : (
                  <div className="placeholder">
                    <span>Upload</span>
                    <p>Click to upload logo</p>
                  </div>
                )}
              </label>
            </div>
            <p className="logo-info">
              This logo will be displayed in transaction PDFs and email
              notifications.
              <br />
              Preferred Dimensions: 240 × 240 pixels @ 72 DPI
              <br />
              Supported Files: jpg, jpeg, png, gif, bmp | Max: 1MB
            </p>
          </div>

          {/* FORM GRID WITH PERFECT ALIGNMENT */}
          <div className="form-grid">
            {/* Organization Name */}
            <div className="org-form-row">
              <label>
                Organization Name <span className="req">*</span>
              </label>
              <div>
                <input name="name" value={form.name} onChange={handleChange} />
                {errors.name && <span className="error">{errors.name}</span>}
              </div>
            </div>

            {/* Industry */}
            <div className="org-form-row">
              <label>
                Industry <span className="req">*</span>
              </label>
              <div>
                <select
                  name="industry"
                  value={form.industry}
                  onChange={handleChange}
                >
                  <option>Technology</option>
                  <option>Agency or Sales House</option>
                  <option>Agriculture</option>
                  <option>Automotive</option>
                  <option>Financial Services</option>
                  <option>Health Care</option>
                  <option>Real Estate</option>
                </select>
              </div>
            </div>

            {/* Location */}
            <div className="org-form-row">
              <label>
                Organization Location <span className="req">*</span>
              </label>
              <div>
                <input
                  name="location"
                  value={form.location}
                  onChange={handleChange}
                />
                {errors.location && (
                  <span className="error">{errors.location}</span>
                )}
              </div>
            </div>

            {/* Address - Full Width */}
            <div className="org-form-row full">
              <label>Organization Address</label>
              <div className="address-grid">
                <input
                  placeholder="Street 1"
                  name="address.street1"
                  onChange={handleChange}
                />
                <input
                  placeholder="Street 2"
                  name="address.street2"
                  onChange={handleChange}
                />
                <input
                  placeholder="City"
                  name="address.city"
                  onChange={handleChange}
                />
                <input
                  placeholder="Pin Code"
                  name="address.pinCode"
                  onChange={handleChange}
                />
                <select
                  name="address.state"
                  value={form.address.state}
                  onChange={handleChange}
                >
                  <option>Tamil Nadu</option>
                  <option>Kerala</option>
                  <option>Karnataka</option>
                  <option>Maharashtra</option>
                </select>
                <input
                  placeholder="Phone"
                  name="address.phone"
                  onChange={handleChange}
                />
                <input
                  placeholder="Fax Number"
                  name="address.fax"
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Website */}
            <div className="org-form-row">
              <label>Website URL</label>
              <div>
                <input
                  name="website"
                  value={form.website}
                  onChange={handleChange}
                  placeholder="https://example.com"
                />
              </div>
            </div>

            {/* Payment Stub Address */}
            <div className="org-form-row full">
              <label>
                Would you like to add a different address for payment stubs?
              </label>
              <div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={form.differentPaymentAddress}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        differentPaymentAddress: e.target.checked,
                      })
                    }
                  />
                  <span className="slider"></span>
                </label>
                {form.differentPaymentAddress && (
                  <textarea
                    placeholder="Payment Stub Address (Max 255 characters)"
                    name="paymentStubAddress"
                    value={form.paymentStubAddress}
                    onChange={handleChange}
                    rows="3"
                    style={{ marginTop: "12px" }}
                  />
                )}
              </div>
            </div>

            {/* Primary Contact */}
            <div className="org-form-row full">
              <label>Primary Contact</label>
              <div className="contact-grid">
                <div className="contact-box">
                  <span>SENDER</span>
                  <p>
                    {form.primaryContact.senderName}
                    <br />
                    {form.primaryContact.senderEmail}
                  </p>
                </div>
                <div className="contact-box">
                  <span>EMAILS ARE SENT THROUGH</span>
                  <p>{form.emailSender}</p>
                </div>
              </div>
            </div>

            {/* Base Currency */}
            <div className="org-form-row">
              <label>Base Currency</label>
              <div>
                <select
                  name="baseCurrency"
                  value={form.baseCurrency}
                  onChange={handleChange}
                >
                  <option>INR - Indian Rupee</option>
                  <option>USD - US Dollar</option>
                </select>
              </div>
            </div>

            {/* Fiscal Year */}
            <div className="org-form-row">
              <label>Fiscal Year</label>
              <div>
                <select
                  name="fiscalYear"
                  value={form.fiscalYear}
                  onChange={handleChange}
                >
                  <option>April - March</option>
                  <option>January - December</option>
                </select>
              </div>
            </div>

            {/* Report Basis */}
            <div className="org-form-row full">
              <label>Report Basis</label>
              <div className="radio-group">
                <label>
                  <input
                    type="radio"
                    name="reportBasis"
                    value="Accrual"
                    checked={form.reportBasis === "Accrual"}
                    onChange={handleChange}
                  />
                  Accrual • You owe tax as of invoice date
                </label>
                <label>
                  <input
                    type="radio"
                    name="reportBasis"
                    value="Cash"
                    checked={form.reportBasis === "Cash"}
                    onChange={handleChange}
                  />
                  Cash • You owe tax upon payment receipt
                </label>
              </div>
            </div>

            {/* Language */}
            <div className="org-form-row">
              <label>Organization Language</label>
              <div>
                <select
                  name="language"
                  value={form.language}
                  onChange={handleChange}
                >
                  <option>English</option>
                  <option>Hindi</option>
                  <option>Tamil</option>
                </select>
              </div>
            </div>

            {/* Communication Language */}
            <div className="org-form-row">
              <label>Communication Language</label>
              <div>
                <select
                  name="communicationLanguage"
                  value={form.communicationLanguage}
                  onChange={handleChange}
                >
                  <option>English</option>
                  <option>Hindi</option>
                </select>
              </div>
            </div>

            {/* Time Zone */}
            <div className="org-form-row">
              <label>Time Zone</label>
              <div>
                <select
                  name="timeZone"
                  value={form.timeZone}
                  onChange={handleChange}
                >
                  <option>
                    (GMT +5:30) India Standard Time (Asia/Calcutta)
                  </option>
                  <option>(GMT +0:00) London</option>
                </select>
              </div>
            </div>

            {/* Date Format */}
            <div className="org-form-row">
              <label>Date Format</label>
              <div className="org-inline">
                <select
                  name="dateFormat"
                  value={form.dateFormat}
                  onChange={handleChange}
                >
                  <option>dd/MM/yyyy</option>
                  <option>MM/dd/yyyy</option>
                  <option>yyyy-MM-dd</option>
                </select>
                <select
                  name="dateSeparator"
                  value={form.dateSeparator}
                  onChange={handleChange}
                >
                  <option>/</option>
                  <option>-</option>
                  <option>.</option>
                </select>
              </div>
            </div>

            {/* Company ID */}
            <div className="org-form-row">
              <label>Company ID</label>
              <div className="org-inline">
                <select
                  name="companyIdPrefix"
                  value={form.companyIdPrefix}
                  onChange={handleChange}
                >
                  <option>ANC</option>
                  <option>BN</option>
                  <option>CN</option>
                </select>
                <input
                  placeholder="0001"
                  name="companyIdValue"
                  value={form.companyIdValue}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Additional Fields */}
            <div className="org-form-row full">
              <label>Additional Fields</label>
              <div>
                <table className="additional-fields">
                  <thead>
                    <tr>
                      <th>LABEL NAME</th>
                      <th>VALUE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.additionalFields.map((field, i) => (
                      <tr key={i}>
                        <td>
                          <input
                            value={field.label}
                            onChange={(e) =>
                              updateField(i, "label", e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <input
                            value={field.value}
                            onChange={(e) =>
                              updateField(i, "value", e.target.value)
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <button onClick={addField} className="add-field">
                  + New Field
                </button>
                <p className="contact-grid1">
                  You can include Company ID and additional fields in your
                  organization address which will be displayed in transaction
                  PDFs.
                </p>
              </div>
            </div>

            {/* Save & Cancel Buttons */}
            <div className="form-actions">
              <button onClick={handleSubmit} className="save-btn">
                Save
              </button>
              <button className="cancel-btn">Cancel</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Organization;
